import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Send,
  AlertTriangle,
  User,
  MapPin,
  Activity,
  FileText,
  Building,
  CheckCircle2,
} from 'lucide-react';
import { useToast, ToastContainer } from '../Toast';
import { esocialService, type ColaboradorParaCat } from '../../services/esocial.service';
import type { CatS2210PayloadContract } from '@sstexames/shared-types';
import StateSelect from '../StateSelect';
import CepInput from '../CepInput';
import { getHojeISO } from '../../utils/dateUtils';

interface CustomerCatWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number;
  onSuccess?: () => void;
}

const TABELA_SITUACAO_GERADORA = [
  { codigo: '301010100', descricao: 'Queda de pessoa com diferença de nível (escada, andaime)' },
  { codigo: '301020100', descricao: 'Queda de pessoa em mesmo nível (escorregão, tropeço)' },
  { codigo: '302010100', descricao: 'Atrito ou abrasão por contato com objeto áspero' },
  { codigo: '303010100', descricao: 'Aprisionamento em, sob ou entre objetos / prensagem' },
  { codigo: '304010100', descricao: 'Impacto sofrido por pessoa contra objeto em movimento' },
  { codigo: '305010100', descricao: 'Esforço excessivo ao erguer, empurrar ou puxar cargas' },
  { codigo: '306010100', descricao: 'Exposição a substâncias químicas ou tóxicas' },
  { codigo: '307010100', descricao: 'Choque elétrico ou contato com corrente elétrica' },
  { codigo: '308010100', descricao: 'Acidente de trânsito durante trajeto ou serviço' },
  { codigo: '999999999', descricao: 'Outras situações geradoras não classificadas' },
];

const TABELA_PARTE_ATINGIDA = [
  { codigo: '752000000', descricao: 'Dedo da mão (quirodáctilo)' },
  { codigo: '751000000', descricao: 'Mão (exceto dedos)' },
  { codigo: '702000000', descricao: 'Punho' },
  { codigo: '701000000', descricao: 'Antebraço' },
  { codigo: '703000000', descricao: 'Braço / Ombro' },
  { codigo: '802000000', descricao: 'Pé e Artelhos (dedos do pé)' },
  { codigo: '801000000', descricao: 'Tornozelo e Perna' },
  { codigo: '803000000', descricao: 'Joelho e Coxa' },
  { codigo: '101000000', descricao: 'Olho / Vista' },
  { codigo: '102000000', descricao: 'Cabeça / Crânio' },
  { codigo: '301000000', descricao: 'Dorso / Coluna vertebral' },
  { codigo: '302000000', descricao: 'Tórax / Abdômen' },
  { codigo: '999999999', descricao: 'Partes múltiplas do corpo' },
];

const TABELA_AGENTE_CAUSADOR = [
  { codigo: '303030100', descricao: 'Máquinas, equipamentos e prensas industriais' },
  { codigo: '301010100', descricao: 'Ferramenta manual ou portátil' },
  { codigo: '401010100', descricao: 'Veículo de transporte rodoviário / automóvel' },
  { codigo: '501010100', descricao: 'Pisos, passadiços, rampas ou superfícies de trabalho' },
  { codigo: '601010100', descricao: 'Eletricidade, cabos elétricos ou transformadores' },
  { codigo: '701010100', descricao: 'Substância química perigosa ou inflamável' },
  { codigo: '801010100', descricao: 'Ser vivo (animal peçonhento, mordedura, agressão)' },
  { codigo: '999999999', descricao: 'Outro agente causador não especificado' },
];

export default function CustomerCatWizardModal({
  isOpen,
  onClose,
  empresaId,
  onSuccess,
}: CustomerCatWizardModalProps) {
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isLoadingColabs, setIsLoadingColabs] = useState(false);
  const [colaboradores, setColaboradores] = useState<ColaboradorParaCat[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Formulário State
  // Passo 1: Vítima e Acidente
  const [colaboradorId, setColaboradorId] = useState<number | ''>('');
  const [tipoAcidente, setTipoAcidente] = useState<'1' | '2' | '3'>('1');
  const [dataAcidente, setDataAcidente] = useState<string>(getHojeISO());
  const [horaAcidente, setHoraAcidente] = useState<string>('08:30');
  const [horasTrabalhadas, setHorasTrabalhadas] = useState<string>('02:00');
  const [houveObito, setHouveObito] = useState<boolean>(false);
  const [dataObito, setDataObito] = useState<string>('');
  const [comunicouPolicia, setComunicouPolicia] = useState<boolean>(false);

  // Passo 2: Local do Acidente
  const [tipoLocal, setTipoLocal] = useState<'1' | '2' | '3'>('1');
  const [descricaoLocal, setDescricaoLocal] = useState<string>('Setor Operacional / Pavilhão Fabril');
  const [cep, setCep] = useState<string>('');
  const [logradouro, setLogradouro] = useState<string>('');
  const [numeroLogradouro, setNumeroLogradouro] = useState<string>('');
  const [bairro, setBairro] = useState<string>('');
  const [uf, setUf] = useState<string>('SP');
  const [codigoMunicipioIbge, setCodigoMunicipioIbge] = useState<string>('3550308');
  const [codigoSituacaoGeradora, setCodigoSituacaoGeradora] = useState<string>('301020100');

  // Passo 3: Lesão e Agente
  const [codigoParteAtingida, setCodigoParteAtingida] = useState<string>('752000000');
  const [lateralidade, setLateralidade] = useState<'0' | '1' | '2' | '3'>('1');
  const [codigoAgenteCausador, setCodigoAgenteCausador] = useState<string>('303030100');
  const [naturezaLesao, setNaturezaLesao] = useState<string>('Corte / Laceração com sangramento local');

  // Passo 4: Atestado Médico
  const [dataAtendimento, setDataAtendimento] = useState<string>(getHojeISO());
  const [horaAtendimento, setHoraAtendimento] = useState<string>('09:15');
  const [houveInternacao, setHouveInternacao] = useState<boolean>(false);
  const [duracaoTratamentoDias, setDuracaoTratamentoDias] = useState<number>(3);
  const [houveAfastamento, setHouveAfastamento] = useState<boolean>(true);
  const [cid10, setCid10] = useState<string>('S61.0');
  const [descricaoLesao, setDescricaoLesao] = useState<string>('Ferimento corto-contuso em extremidade digital');
  const [medicoNome, setMedicoNome] = useState<string>('');
  const [medicoConselho, setMedicoConselho] = useState<'1' | '2'>('1');
  const [medicoNumero, setMedicoNumero] = useState<string>('');
  const [medicoUf, setMedicoUf] = useState<string>('SP');

  const loadColaboradores = useCallback(async () => {
    setIsLoadingColabs(true);
    try {
      const data = await esocialService.listColaboradoresParaCat();
      setColaboradores(data);
    } catch (err) {
      console.error('Erro ao carregar colaboradores para CAT:', err);
      toast.error('Não foi possível carregar a lista de colaboradores.');
    } finally {
      setIsLoadingColabs(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isOpen) {
      loadColaboradores();
    }
  }, [isOpen, empresaId, loadColaboradores]);

  if (!isOpen) return null;

  const validarPasso1 = () => {
    if (!colaboradorId) {
      toast.error('Selecione o colaborador acidentado.');
      return false;
    }
    if (!dataAcidente) {
      toast.error('Informe a data do acidente.');
      return false;
    }
    if (!horaAcidente) {
      toast.error('Informe o horário do acidente.');
      return false;
    }
    if (houveObito && !dataObito) {
      toast.error('Informe a data do óbito.');
      return false;
    }
    return true;
  };

  const validarPasso2 = () => {
    if (!descricaoLocal.trim()) {
      toast.error('Informe a descrição do local do acidente.');
      return false;
    }
    if (!codigoSituacaoGeradora) {
      toast.error('Selecione a situação geradora do acidente.');
      return false;
    }
    return true;
  };

  const validarPasso3 = () => {
    if (!codigoParteAtingida) {
      toast.error('Selecione a parte do corpo atingida.');
      return false;
    }
    if (!codigoAgenteCausador) {
      toast.error('Selecione o agente causador do acidente.');
      return false;
    }
    return true;
  };

  const validarPasso4 = () => {
    if (!dataAtendimento || !horaAtendimento) {
      toast.error('Informe a data e o horário do atendimento médico.');
      return false;
    }
    if (!cid10.trim()) {
      toast.error('Informe o código CID-10 do diagnóstico.');
      return false;
    }
    if (!medicoNome.trim()) {
      toast.error('Informe o nome do médico emitente do atestado.');
      return false;
    }
    if (!medicoNumero.trim()) {
      toast.error('Informe o número do CRM/CRO do médico.');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validarPasso1()) return;
    if (currentStep === 2 && !validarPasso2()) return;
    if (currentStep === 3 && !validarPasso3()) return;
    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validarPasso4()) return;

    setIsSubmitting(true);
    try {
      const payload: CatS2210PayloadContract = {
        empresa_id: empresaId,
        colaborador_id: Number(colaboradorId),
        tipo_acidente: tipoAcidente,
        data_acidente: dataAcidente,
        hora_acidente: horaAcidente.replace(/\D/g, '').padEnd(4, '0').slice(0, 4),
        horas_trabalhadas_antes_acidente: horasTrabalhadas ? horasTrabalhadas.replace(/\D/g, '').slice(0, 4) : undefined,
        houve_obito: houveObito,
        data_obito: houveObito ? dataObito : undefined,
        comunicou_policia: comunicouPolicia,
        tipo_local: tipoLocal,
        descricao_local: descricaoLocal,
        cep: cep ? cep.replace(/\D/g, '') : undefined,
        logradouro: logradouro || undefined,
        numero_logradouro: numeroLogradouro || undefined,
        bairro: bairro || undefined,
        codigo_municipio_ibge: codigoMunicipioIbge || undefined,
        uf: uf || undefined,
        codigo_parte_atingida: codigoParteAtingida,
        lateralidade: lateralidade,
        codigo_agente_causador: codigoAgenteCausador,
        codigo_situacao_geradora: codigoSituacaoGeradora,
        natureza_lesao: naturezaLesao || undefined,
        atestado: {
          data_atendimento: dataAtendimento,
          hora_atendimento: horaAtendimento.replace(/\D/g, '').padEnd(4, '0').slice(0, 4),
          houve_internacao: houveInternacao,
          duracao_tratamento_dias: Number(duracaoTratamentoDias) || 1,
          houve_afastamento: houveAfastamento,
          descricao_lesao: descricaoLesao || undefined,
          cid_10: cid10.trim().toUpperCase(),
          medico_emitente: {
            nome: medicoNome.trim(),
            conselho: medicoConselho,
            numero_conselho: medicoNumero.trim(),
            uf_conselho: medicoUf,
          },
        },
      };

      const result = await esocialService.criarCatS2210(payload);
      toast.success(`CAT (S-2210) emitida com sucesso! Evento #${result.id}`);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1200);
    } catch (err: unknown) {
      console.error('Erro ao emitir CAT:', err);
      const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = errorObj?.response?.data?.message || errorObj?.message || 'Falha ao registrar CAT';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { num: 1, title: 'Vítima & Acidente', icon: User },
    { num: 2, title: 'Local do Evento', icon: MapPin },
    { num: 3, title: 'Lesão & Agente', icon: Activity },
    { num: 4, title: 'Atestado Médico', icon: FileText },
  ];

  const colabSelecionado = colaboradores.find((c) => c.id === Number(colaboradorId));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden border border-gray-100 flex flex-col max-h-[92vh]">
        {/* Header Moderno */}
        <div className="bg-gradient-to-r from-emerald-700 via-teal-700 to-emerald-900 px-6 py-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-xl backdrop-blur-md border border-white/20">
              <Building className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold tracking-tight">Emissão de CAT (S-2210)</h2>
                <span className="bg-emerald-500/30 text-emerald-100 text-xs px-2.5 py-0.5 rounded-full font-medium border border-emerald-400/30">
                  eSocial Oficial
                </span>
              </div>
              <p className="text-xs text-emerald-100/80 mt-0.5">
                Comunicação de Acidente de Trabalho ou Doença Ocupacional
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper Progress */}
        <div className="bg-emerald-50/50 border-b border-emerald-100/60 px-6 py-3.5">
          <div className="grid grid-cols-4 gap-2">
            {steps.map((s) => {
              const Icon = s.icon;
              const isPast = currentStep > s.num;
              const isCurr = currentStep === s.num;

              return (
                <div
                  key={s.num}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all ${
                    isCurr
                      ? 'bg-white shadow-sm border border-emerald-200 text-emerald-900 font-semibold'
                      : isPast
                        ? 'text-emerald-700 font-medium'
                        : 'text-gray-400'
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      isCurr
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : isPast
                          ? 'bg-emerald-200 text-emerald-800'
                          : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {isPast ? <CheckCircle2 className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="text-xs truncate hidden sm:inline">{s.title}</span>
                  <Icon className="w-3.5 h-3.5 hidden md:inline ml-auto text-emerald-600/70" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Body Scrollable */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* PASSO 1: VÍTIMA E ACIDENTE */}
          {currentStep === 1 && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-xs text-emerald-900 leading-relaxed">
                  <strong>Atenção ao Prazo eSocial:</strong> A CAT deve ser emitida até o 1º dia útil seguinte ao da ocorrência e, em caso de morte, de imediato.
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Colaborador Acidentado <span className="text-red-500 font-bold">*</span>
                </label>
                {isLoadingColabs ? (
                  <div className="text-sm text-gray-500 py-2">Carregando lista de colaboradores...</div>
                ) : (
                  <select
                    value={colaboradorId}
                    onChange={(e) => setColaboradorId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full rounded-xl border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm py-2.5"
                    required
                  >
                    <option value="">Selecione um colaborador da empresa...</option>
                    {colaboradores.map((colab) => (
                      <option key={colab.id} value={colab.id}>
                        {colab.nome} — CPF: {colab.cpf} {colab.cargo_nome ? `(${colab.cargo_nome})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {colabSelecionado && (
                  <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-200 flex flex-wrap gap-4">
                    <span><strong>Matrícula eSocial:</strong> {colabSelecionado.matricula_esocial || 'Padrão'}</span>
                    {colabSelecionado.cargo_nome && <span><strong>Cargo:</strong> {colabSelecionado.cargo_nome}</span>}
                    {colabSelecionado.setor_nome && <span><strong>Setor:</strong> {colabSelecionado.setor_nome}</span>}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Tipo de Acidente <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={tipoAcidente}
                    onChange={(e) => setTipoAcidente(e.target.value as '1' | '2' | '3')}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    <option value="1">1 - Típico (No local de trabalho)</option>
                    <option value="2">2 - Trajeto (Residência / Trabalho)</option>
                    <option value="3">3 - Doença Ocupacional / Profissional</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Data do Acidente <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="date"
                    value={dataAcidente}
                    onChange={(e) => setDataAcidente(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Horário do Acidente <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="time"
                    value={horaAcidente}
                    onChange={(e) => setHoraAcidente(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Horas Trabalhadas antes do Acidente
                  </label>
                  <input
                    type="time"
                    value={horasTrabalhadas}
                    onChange={(e) => setHorasTrabalhadas(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Houve Óbito? <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={houveObito ? 'sim' : 'nao'}
                    onChange={(e) => setHouveObito(e.target.value === 'sim')}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim (Comunicação imediata)</option>
                  </select>
                </div>

                {houveObito && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Data do Óbito <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="date"
                      value={dataObito}
                      onChange={(e) => setDataObito(e.target.value)}
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Comunicou Autoridade Policial?
                  </label>
                  <select
                    value={comunicouPolicia ? 'sim' : 'nao'}
                    onChange={(e) => setComunicouPolicia(e.target.value === 'sim')}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* PASSO 2: LOCAL E CIRCUNSTÂNCIAS */}
          {currentStep === 2 && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Tipo do Local do Acidente <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={tipoLocal}
                    onChange={(e) => setTipoLocal(e.target.value as '1' | '2' | '3')}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    <option value="1">1 - Estabelecimento do Empregador no Brasil</option>
                    <option value="2">2 - Estabelecimento de Terceiros / Cliente</option>
                    <option value="3">3 - Via Pública</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Situação Geradora (Tabela 16 eSocial) <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={codigoSituacaoGeradora}
                    onChange={(e) => setCodigoSituacaoGeradora(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    {TABELA_SITUACAO_GERADORA.map((item) => (
                      <option key={item.codigo} value={item.codigo}>
                        {item.descricao}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Descrição Detalhada do Local / Circunstância <span className="text-red-500 font-bold">*</span>
                </label>
                <textarea
                  rows={2}
                  value={descricaoLocal}
                  onChange={(e) => setDescricaoLocal(e.target.value)}
                  placeholder="Ex: Escada de acesso à linha de produção número 3, piso molhado..."
                  className="w-full rounded-xl border-gray-300 text-sm"
                  required
                />
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                  Endereço do Acidente
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">CEP</label>
                    <CepInput
                      value={cep}
                      onChange={setCep}
                      onAddressFound={(end) => {
                        if (end.logradouro) setLogradouro(end.logradouro);
                        if (end.bairro) setBairro(end.bairro);
                        if (end.uf) setUf(end.uf);
                        if (end.ibge) setCodigoMunicipioIbge(end.ibge);
                      }}
                      className="rounded-xl border-gray-300 text-sm"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Logradouro</label>
                    <input
                      type="text"
                      value={logradouro}
                      onChange={(e) => setLogradouro(e.target.value)}
                      placeholder="Rua, Avenida, Praça..."
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Número</label>
                    <input
                      type="text"
                      value={numeroLogradouro}
                      onChange={(e) => setNumeroLogradouro(e.target.value)}
                      placeholder="Ex: 120"
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Bairro</label>
                    <input
                      type="text"
                      value={bairro}
                      onChange={(e) => setBairro(e.target.value)}
                      placeholder="Bairro"
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Estado / UF</label>
                    <StateSelect
                      value={uf}
                      onChange={setUf}
                      className="rounded-xl border-gray-300 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Código IBGE Município</label>
                    <input
                      type="text"
                      value={codigoMunicipioIbge}
                      onChange={(e) => setCodigoMunicipioIbge(e.target.value)}
                      placeholder="Ex: 3550308"
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PASSO 3: LESÃO E PARTE ATINGIDA */}
          {currentStep === 3 && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Parte do Corpo Atingida (Tabela 13 eSocial) <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={codigoParteAtingida}
                    onChange={(e) => setCodigoParteAtingida(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    {TABELA_PARTE_ATINGIDA.map((item) => (
                      <option key={item.codigo} value={item.codigo}>
                        {item.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Lateralidade <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={lateralidade}
                    onChange={(e) => setLateralidade(e.target.value as '0' | '1' | '2' | '3')}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    <option value="0">0 - Não aplicável</option>
                    <option value="1">1 - Esquerda</option>
                    <option value="2">2 - Direita</option>
                    <option value="3">3 - Ambas</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Agente Causador (Tabela 14/15 eSocial) <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={codigoAgenteCausador}
                    onChange={(e) => setCodigoAgenteCausador(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    {TABELA_AGENTE_CAUSADOR.map((item) => (
                      <option key={item.codigo} value={item.codigo}>
                        {item.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Natureza da Lesão (Tabela 17 eSocial)
                  </label>
                  <input
                    type="text"
                    value={naturezaLesao}
                    onChange={(e) => setNaturezaLesao(e.target.value)}
                    placeholder="Ex: Corte, fratura, luxação, queimadura..."
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* PASSO 4: ATESTADO MÉDICO */}
          {currentStep === 4 && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Data do Atendimento Médico <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="date"
                    value={dataAtendimento}
                    onChange={(e) => setDataAtendimento(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Hora do Atendimento <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="time"
                    value={horaAtendimento}
                    onChange={(e) => setHoraAtendimento(e.target.value)}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    CID-10 Principal <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="text"
                    value={cid10}
                    onChange={(e) => setCid10(e.target.value)}
                    placeholder="Ex: S61.0"
                    className="w-full rounded-xl border-gray-300 text-sm py-2 font-mono"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Houve Internação? <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={houveInternacao ? 'sim' : 'nao'}
                    onChange={(e) => setHouveInternacao(e.target.value === 'sim')}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    <option value="nao">Não</option>
                    <option value="sim">Sim</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Houve Afastamento? <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    value={houveAfastamento ? 'sim' : 'nao'}
                    onChange={(e) => setHouveAfastamento(e.target.value === 'sim')}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                  >
                    <option value="sim">Sim</option>
                    <option value="nao">Não</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Duração do Tratamento (dias) <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={duracaoTratamentoDias}
                    onChange={(e) => setDuracaoTratamentoDias(Number(e.target.value))}
                    className="w-full rounded-xl border-gray-300 text-sm py-2"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Descrição Clínica da Lesão (conforme atestado)
                </label>
                <textarea
                  rows={2}
                  value={descricaoLesao}
                  onChange={(e) => setDescricaoLesao(e.target.value)}
                  placeholder="Ex: Sutura realizada em falange distal..."
                  className="w-full rounded-xl border-gray-300 text-sm"
                />
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                  Médico / Dentista Emitente
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Nome do Profissional <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      value={medicoNome}
                      onChange={(e) => setMedicoNome(e.target.value)}
                      placeholder="Dr(a). Nome Sobrenome"
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Conselho <span className="text-red-500 font-bold">*</span>
                    </label>
                    <select
                      value={medicoConselho}
                      onChange={(e) => setMedicoConselho(e.target.value as '1' | '2')}
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                    >
                      <option value="1">1 - CRM (Medicina)</option>
                      <option value="2">2 - CRO (Odontologia)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Número no Conselho <span className="text-red-500 font-bold">*</span>
                    </label>
                    <input
                      type="text"
                      value={medicoNumero}
                      onChange={(e) => setMedicoNumero(e.target.value)}
                      placeholder="Ex: 123456"
                      className="w-full rounded-xl border-gray-300 text-sm py-2"
                      required
                    />
                  </div>
                </div>

                <div className="mt-3 w-48">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    UF do Conselho <span className="text-red-500 font-bold">*</span>
                  </label>
                  <StateSelect
                    value={medicoUf}
                    onChange={setMedicoUf}
                    className="rounded-xl border-gray-300 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer com botões de navegação */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrev}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
                Voltar
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-200/60 transition-colors"
            >
              Cancelar
            </button>

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-all"
              >
                Próximo Passo
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-md shadow-emerald-600/20 disabled:opacity-50 transition-all"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando eSocial...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Emitir CAT (S-2210)
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
}
